import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useStoreIndents } from "@/features/store-indents/api";
import { useStoreAllocations } from "@/features/store-allocations/api";
import type { StoreIndentKind } from "@/features/store-indents/types";
import { useBranch } from "@/context/BranchContext";
import { formatDate } from "@/features/assets/format";
import { SearchInput } from "@/components/shared/SearchInput";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { searchEmptyMessage } from "@/lib/listSearch";


export const Route = createFileRoute("/_authenticated/store-indents/")({
  component: StoreIndentsIndex,
  validateSearch: (s: Record<string, unknown>) => ({ kind: (s.kind as string) || "all" }),
});

function StoreIndentsIndex() {
  const navigate = useNavigate();
  const { kind: initKind } = Route.useSearch();
  const [kind, setKind] = useState<StoreIndentKind | "all">(
    (["all", "spare", "consumable", "asset"] as const).includes(initKind as never) ? (initKind as StoreIndentKind | "all") : "all",
  );
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search);
  const { data = [], isLoading } = useStoreIndents({
    kind: kind === "all" ? undefined : kind,
    branchId: effectiveBranchId,
    search: term,
  });
  const { data: allAllocations } = useStoreAllocations();

  return (
    <div className="min-w-0 max-w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Store Indents</h1>
          <p className="text-sm text-muted-foreground">Department requisitions for spares, consumables and asset allocations.</p>
        </div>
        <Button onClick={() => navigate({ to: "/store-indents/new", search: { kind: kind === "all" ? "spare" : kind } })}>
          <Plus className="h-4 w-4 mr-1" />New Store Indent
        </Button>
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
      <Tabs value={kind} onValueChange={(v) => setKind(v as StoreIndentKind | "all")} className="min-w-0 max-w-full">
        <TabsList className="h-auto max-w-full flex-wrap justify-start">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="spare">Spares</TabsTrigger>
          <TabsTrigger value="consumable">Consumables</TabsTrigger>
          <TabsTrigger value="asset">Asset Allocation</TabsTrigger>
          <TabsTrigger value="stationery">Stationery</TabsTrigger>
          <TabsTrigger value="mixed">Mixed</TabsTrigger>
        </TabsList>
      </Tabs>
      <SearchInput value={search} onChange={setSearch} placeholder="Search SI no, department, requester or item" label="Search Store Indents" />
      </div>

      {/* the Table component wraps its <table> in an overflow-auto box, so on a narrow screen only the table scrolls sideways */}
      <Card className="min-w-0 max-w-full"><CardContent className="min-w-0 p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          data.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">{searchEmptyMessage(term, "Store Indents", "No store indents.")}</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Indent No</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ack Status</TableHead>
              <TableHead>Raised On</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((r) => {
                const indentAllocations = (allAllocations ?? []).filter(a => a.store_indent_id === r.id);
                const hasDefective = indentAllocations.some(a => a.ack_status === "Defective");
                const allGood = indentAllocations.length > 0 && indentAllocations.every(a => a.ack_status === "Good");
                const somePending = indentAllocations.some(a => !a.ack_status);

                return (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/store-indents/$id", params: { id: r.id } })}>
                    <TableCell className="font-medium">{r.store_indent_no}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{r.display_kind ?? r.kind}</Badge></TableCell>
                    <TableCell>{r.requester?.full_name || r.requester?.email || "—"}</TableCell>
                    <TableCell>{r.department?.name ?? "—"}</TableCell>
                    <TableCell>{r.branch?.name ?? "—"}</TableCell>
                    <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                    <TableCell>
                      {hasDefective ? <Badge variant="destructive">Returned</Badge>
                        : allGood ? <Badge variant="default" className="bg-green-600 hover:bg-green-700">Accepted</Badge>
                        : indentAllocations.length > 0 && somePending ? <Badge variant="secondary">Pending Ack</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>{formatDate(r.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
